import { catchError, map, Observable, of } from "rxjs";
import { Injectable } from '@angular/core';

import { IQuoteListApiResponse, IQuote } from "@modules/private/quote/data/interfaces/iquote.interface";
import { FilterOptions } from '@shared/interfaces/filterOptions.interface';
import { AuthService } from "@data/services/modules/auth/auth.service";
import { ApiClass } from "@data/class/api-class";
import { UserRole } from "@data/enums/user-role.enum";
import { environment } from "@env/environment";

@Injectable({
  providedIn: 'root'
})
export class ApiQuoteService extends ApiClass {
  user!: string
  id!: string

  constructor(
    private readonly authService: AuthService
  ) {
    super()
    this.path = 'quotations'
    this.user = this.authService.getUser?.user.permissions.name || ''
    this.id = this.authService.getUser?.user.id || ''
  }

  /**
   * Obtener todas las cotizaciones calculadas (solo lectura)
   * IMPORTANTE: Solo para visualización de cotizaciones ya calculadas
   */
  getAll(page = 1, limit = 10, filters = {}): Observable<IQuoteListApiResponse> {
    this.path = 'quotations'

    // Filtro client-side solo para Broker (ownership-restringido): mantiene paridad
    // con el backend y minimiza payload. El Seller ya NO se filtra (ADR-0020): salió
    // de OWNERSHIP_RESTRICTED_ROLES y el endpoint server-side le devuelve todas las
    // cotizaciones. MasterBroker/Client se manejan server-side, no aquí.
    if (this.user === UserRole.BROKER) {
      filters = { ...filters, user: { value: this.id, matchMode: 'equals' } }
    }

    const response: IQuoteListApiResponse = { 
      error: true, 
      msg: 'Error de conexión', 
      data: {
        data: [],
        total: 0
      }, 
      status: 500, 
      page: 1, 
      itemsByPage: 10, 
      totalItems: 0 
    }

    return this.http.post<IQuoteListApiResponse>(environment.apiUrl + this.path, { page, limit, filters }).pipe(
      map((r) => r as IQuoteListApiResponse),
      catchError((e) => of(response))
    )
  }

  /**
   * Obtener cotización específica por ID (solo lectura)
   * IMPORTANTE: Adaptado para nueva estructura con termData y amortizationPeriods
   */
  getById(id: string): Observable<{ error: boolean, msg: string, data: IQuote }> {
    return this.getCollection(id).pipe(
      map((r) => r as { error: boolean, msg: string, data: IQuote }),
      catchError((error) => {
        console.error('Error al obtener cotización:', error);
        return of({ error: true, msg: 'Error al obtener cotización', data: {} as IQuote });
      })
    )
  }

  /**
   * Calcular cotización desde parámetros (nuevo método)
   * IMPORTANTE: Calcula una cotización desde QuoteParameters en tiempo real
   */
  calculateQuoteFromParameters(parametersId: string): Observable<{ error: boolean, msg: string, data: IQuote }> {
    const endpoint = `quotation-parameters/${parametersId}/calculate`;
    return this.http.post<{ error: boolean, msg: string, data: IQuote }>(environment.apiUrl + endpoint, {}).pipe(
      catchError((error) => {
        console.error('Error al calcular cotización desde parámetros:', error);
        return of({ error: true, msg: 'Error al calcular cotización', data: {} as IQuote });
      })
    )
  }

  /**
   * Cancelar cotización (solo cambio de estado)
   * IMPORTANTE: No elimina, solo cambia status a 'Cancelada'
   */
  cancel(id: string): Observable<{ error: boolean, msg: string, data: any }> {
    const endpoint = `quotations/${id}/cancel`;
    return this.http.put<{ error: boolean, msg: string, data: any }>(environment.apiUrl + endpoint, {}).pipe(
      catchError((error) => {
        console.error('Error al cancelar cotización:', error);
        return of({ error: true, msg: 'Error al cancelar cotización', data: null });
      })
    )
  }

  // ===== MÉTODOS LEGACY REMOVIDOS =====
  // Los siguientes métodos han sido movidos a ApiQuoteParametersService:
  // - create() -> Ahora se crean QuoteParameters
  // - update() -> Ahora se actualizan QuoteParameters  
  // - delete() -> Ahora se eliminan QuoteParameters
  // - getAmounts() -> Ahora se usa calculateQuoteFromParameters()

  // ===== MÉTODOS REUTILIZADOS PARA COMPATIBILIDAD =====
  // Estos métodos se mantienen para compatibilidad con componentes legacy

  /**
   * Obtener clientes (mantenido para compatibilidad)
   * NOTA: También disponible en ApiQuoteParametersService
   */
  fetchClients(filterOptions: FilterOptions): Observable<{ label: string, value: string }[]> {
    filterOptions.limit = 100;
    this.path = 'clients'

    return this.postCollection(filterOptions).pipe(
      map((response) => {
        if (!response.error && Array.isArray(response.data.data)) {
          return response.data.data.map((client: { _id: string, name: string }) => ({
            label: client.name,
            value: client._id
          }));
        } else {
          console.error('Error en la respuesta o los datos no son un array:', response.msg);
          return [];
        }
      }),
      catchError((error) => {
        console.error('Error al obtener los clientes:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtener activos (mantenido para compatibilidad)
   * NOTA: También disponible en ApiQuoteParametersService
   */
  fetchAssets(filterOptions: FilterOptions): Observable<{ label: string, value: string, amount: number }[]> {
    filterOptions.limit = 100;
    filterOptions.filters = { status: { value: 'Arrendado', matchMode: 'notEquals' } };
    this.path = 'assets';

    return this.postCollection(filterOptions).pipe(
      map(response => {
        if (!response.error && Array.isArray(response.data.data)) {
          return response.data.data.map((asset: { _id: string, name: string, acquisition: { total: number } }) => ({
            label: asset.name,
            value: asset._id,
            amount: asset.acquisition.total
          }));
        } else {
          console.error('Error en la respuesta o los datos no son un array:', response.msg);
          return [];
        }
      }),
      catchError(error => {
        console.error('Error al obtener los activos:', error);
        return of([]);
      })
    );
  }
}
